import React, { useMemo } from 'react';
import { Box, Typography, alpha, useTheme } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { DAY_END_HOUR, DAY_START_HOUR, HOUR_ROW_HEIGHT, pastDaySx, todayColumnSx, todayHeaderSx, focusVisibleSx } from './stiklainisStyles';
import ReservationBlock from './ReservationBlock';
import { Reservation } from './types';
import { getWeekDays, isPastDay, isToday } from './slotUtils';

interface StiklainisWeekViewProps {
  currentDate: Date;
  reservations: Reservation[];
  selectedRoomId: string;
  currentUserId?: string;
  dayNames: string[];
  onDayClick: (day: Date, e: React.MouseEvent<HTMLDivElement>) => void;
  onReservationClick: (reservation: Reservation, isOwn: boolean) => void;
}

const StiklainisWeekView: React.FC<StiklainisWeekViewProps> = ({
  currentDate,
  reservations,
  selectedRoomId,
  currentUserId,
  dayNames,
  onDayClick,
  onReservationClick,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const totalHours = DAY_END_HOUR - DAY_START_HOUR;
  const totalHeight = totalHours * HOUR_ROW_HEIGHT;
  const weekDays = getWeekDays(currentDate);

  const now = new Date();
  const currentTimeTop = useMemo(() => {
    const hours = now.getHours() + now.getMinutes() / 60;
    if (hours < DAY_START_HOUR || hours > DAY_END_HOUR) return null;
    return (hours - DAY_START_HOUR) * HOUR_ROW_HEIGHT;
  }, [now.getHours(), now.getMinutes()]);

  const getReservationsForDay = (day: Date) =>
    reservations
      .filter((r) => {
        const start = new Date(r.startTime);
        return r.room.id === selectedRoomId &&
          start.getFullYear() === day.getFullYear() &&
          start.getMonth() === day.getMonth() &&
          start.getDate() === day.getDate();
      })
      .sort((a, b) => new Date(a.startTime).getTime() - new Date(b.startTime).getTime());

  const computeBlockPosition = (reservation: Reservation) => {
    const start = new Date(reservation.startTime);
    const end = new Date(reservation.endTime);
    const startMinutes = (start.getHours() - DAY_START_HOUR) * 60 + start.getMinutes();
    const endMinutes = (end.getHours() - DAY_START_HOUR) * 60 + end.getMinutes();
    const top = Math.max(0, (startMinutes / 60) * HOUR_ROW_HEIGHT);
    const bottom = Math.max(0, totalHeight - Math.max(0, (endMinutes / 60) * HOUR_ROW_HEIGHT));
    const height = Math.max(22, totalHeight - top - bottom);
    return { top, height };
  };

  return (
    <Box sx={{ overflowX: 'auto' }} role="region" aria-label={t('stiklainis.weekView')}>
      <Box sx={{ display: 'flex', mb: 0, position: 'sticky', top: 57, zIndex: 3, bgcolor: 'background.paper', borderBottom: 1, borderColor: 'divider' }}>
        <Box sx={{ width: 64, flexShrink: 0 }} />
        {weekDays.map((day) => {
          const past = isPastDay(day);
          const today = isToday(day);
          return (
            <Box
              key={day.toISOString()}
              sx={{
                flex: 1,
                minWidth: 112,
                p: 1,
                textAlign: 'center',
                ...(past && pastDaySx(theme)),
                ...(today && !past && todayHeaderSx(theme)),
              }}
            >
              <Typography variant="caption" sx={{ fontWeight: 600, display: 'block', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                {dayNames[day.getDay() === 0 ? 6 : day.getDay() - 1]}
              </Typography>
              <Typography variant="body1" sx={{ fontWeight: 700 }}>
                {day.getDate()}
              </Typography>
            </Box>
          );
        })}
      </Box>

      <Box sx={{ display: 'flex' }}>
        <Box sx={{ width: 64, flexShrink: 0, position: 'relative' }} aria-hidden>
          {Array.from({ length: totalHours + 1 }).map((_, idx) => (
            <Box key={idx} sx={{ height: HOUR_ROW_HEIGHT, position: 'relative', borderTop: idx === 0 ? 'none' : '1px solid', borderColor: 'divider' }}>
              <Typography variant="caption" color="text.secondary" sx={{ position: 'absolute', top: -8, right: 8, fontSize: '0.7rem' }}>
                {String(DAY_START_HOUR + idx).padStart(2, '0')}:00
              </Typography>
            </Box>
          ))}
        </Box>

        {weekDays.map((day) => {
          const past = isPastDay(day);
          const today = isToday(day);
          const dayReservations = getReservationsForDay(day);

          let lastBottom = -Infinity;
          const gapPx = 2;
          const dayLabel = day.toLocaleDateString();

          return (
            <Box key={day.toISOString()} sx={{ flex: 1, minWidth: 112, p: 0.25 }}>
              <Box
                role="button"
                tabIndex={0}
                aria-label={t('stiklainis.addReservationOnDay') + ` (${dayLabel})`}
                onClick={(e) => onDayClick(day, e)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    const fakeEvent = {
                      currentTarget: e.currentTarget,
                      clientY: e.currentTarget.getBoundingClientRect().top + HOUR_ROW_HEIGHT,
                    } as unknown as React.MouseEvent<HTMLDivElement>;
                    onDayClick(day, fakeEvent);
                  }
                }}
                sx={{
                  position: 'relative',
                  height: totalHeight,
                  borderLeft: '1px solid',
                  borderColor: past ? alpha(theme.palette.grey[500], 0.3) : 'divider',
                  cursor: 'pointer',
                  '&:hover': { bgcolor: past ? alpha(theme.palette.grey[500], 0.12) : 'action.hover' },
                  backgroundImage: `repeating-linear-gradient(to bottom, transparent, transparent ${HOUR_ROW_HEIGHT - 1}px, ${alpha(theme.palette.divider, 0.6)} ${HOUR_ROW_HEIGHT - 1}px, ${alpha(theme.palette.divider, 0.6)} ${HOUR_ROW_HEIGHT}px)`,
                  ...(past && pastDaySx(theme)),
                  ...(today && !past && todayColumnSx(theme)),
                  ...focusVisibleSx,
                }}
              >
                {today && currentTimeTop !== null && (
                  <Box
                    sx={{
                      position: 'absolute',
                      left: 0,
                      right: 0,
                      top: currentTimeTop,
                      height: 2,
                      bgcolor: 'error.main',
                      zIndex: 5,
                      pointerEvents: 'none',
                      '&::before': {
                        content: '""',
                        position: 'absolute',
                        left: -4,
                        top: -3,
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        bgcolor: 'error.main',
                      },
                    }}
                  />
                )}

                {dayReservations.map((reservation) => {
                  let { top, height } = computeBlockPosition(reservation);
                  if (Number.isFinite(lastBottom) && top <= lastBottom + 0.5) {
                    top = lastBottom + gapPx;
                    const end = new Date(reservation.endTime);
                    const endMinutes = (end.getHours() - DAY_START_HOUR) * 60 + end.getMinutes();
                    const bottom = Math.max(0, totalHeight - Math.max(0, (endMinutes / 60) * HOUR_ROW_HEIGHT));
                    height = Math.max(22, totalHeight - top - bottom);
                  }
                  lastBottom = top + height;

                  const isOwn = Boolean(currentUserId && reservation.user.id === currentUserId);

                  return (
                    <ReservationBlock
                      key={reservation.id}
                      reservation={reservation}
                      isOwn={isOwn}
                      top={top}
                      height={height}
                      onClick={(e) => {
                        e.stopPropagation();
                        onReservationClick(reservation, isOwn);
                      }}
                    />
                  );
                })}
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default StiklainisWeekView;
